import { useTracking, useVideoTracking } from '@folklore/tracking';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, ReactNode, use, useEffect, useRef, useState } from 'react';

import { fetchDocument } from '../hooks/useDocument';

import { useApi } from './ApiContext';

const step = 0.01;
const progressSteps = Array.from({ length: Math.round(1 / step) }, (_, i) => (i + 1) * step);

export const audioPlaybackRates = [1, 1.25, 1.5, 2, 0.75];

export function getAudioUrl(audio: Audio | null = null): string | null {
    const { url = null, sources = null } = audio || {};
    return url || sources?.[0]?.url || null;
}

// A podcast episode that can be listened to in the audio player (it may also have a video)
export function hasAudio(document: Partial<PodcastDocument> | null = null): boolean {
    const { type = null, audio = null } = document || {};
    return type === 'podcast' && getAudioUrl(audio) !== null;
}

function isPodcastCollectionType(type: string | null = null): boolean {
    return type === 'serie_podcast' || type === 'season_podcast';
}

// An episode of a podcast, audio or video: its page shows the playlist of its season
export function hasEpisodeLayout(document: Partial<PodcastDocument> | null = null): boolean {
    const { type = null, metadata = null } = document || {};
    if (type === 'podcast') {
        return true;
    }
    const { collections = [] } = metadata || {};
    return (
        type === 'video' &&
        (collections || []).some(
            ({ type: collectionType = null, serie = null }) =>
                isPodcastCollectionType(collectionType) || isPodcastCollectionType(serie?.type),
        )
    );
}

interface LoadOptions {
    queue?: DocumentSnippet[] | null;
    startTime?: number | null;
    autoPlay?: boolean;
    withoutTracking?: boolean;
}

interface AudioPlayerContextValue {
    document: PodcastDocument | null;
    queue: DocumentSnippet[];
    nextItem: DocumentSnippet | null;
    playing: boolean;
    loading: boolean;
    currentTime: number;
    duration: number;
    rate: number;
    muted: boolean;
    autoplayNext: boolean;
    pagePlayerVisible: boolean;
    load: (document: PodcastDocument, opts?: LoadOptions) => void;
    play: () => void;
    pause: () => void;
    toggle: () => void;
    seek: (time: number) => void;
    skip: (delta: number) => void;
    setRate: (rate: number) => void;
    setMuted: (muted: boolean) => void;
    setQueue: (queue: DocumentSnippet[]) => void;
    setAutoplayNext: (autoplayNext: boolean) => void;
    playItem: (item: DocumentSnippet, opts?: { keepVideo?: boolean }) => void;
    next: () => void;
    close: () => void;
    setPagePlayerVisible: (key: string, documentId: string | null) => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextValue | null>(null);

export function useAudioPlayer(): AudioPlayerContextValue | null {
    return use(AudioPlayerContext);
}

interface AudioPlayerProviderProps {
    children?: ReactNode;
}

export function AudioPlayerProvider({ children = null }: AudioPlayerProviderProps) {
    const api = useApi();
    const queryClient = useQueryClient();
    const tracking = useTracking();
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const pendingSeekRef = useRef<number | null>(null);
    const startedRef = useRef<Record<string, boolean>>({});

    const [document, setDocument] = useState<PodcastDocument | null>(null);
    const [queue, setQueue] = useState<DocumentSnippet[]>([]);
    const [playing, setPlaying] = useState(false);
    const [waiting, setWaiting] = useState(false);
    const [ended, setEnded] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [rate, setRateState] = useState(1);
    const [muted, setMutedState] = useState(false);
    const [autoplayNext, setAutoplayNext] = useState(true);
    const [trackingDisabled, setTrackingDisabled] = useState(false);
    const [pagePlayers, setPagePlayers] = useState<Record<string, string>>({});

    const { id: documentId = null, title = null, audio = null } = document || {};
    const currentIndex = queue.findIndex(({ id }) => id === documentId);
    const nextItem = currentIndex !== -1 ? queue[currentIndex + 1] || null : null;
    const pagePlayerVisible =
        documentId !== null && Object.values(pagePlayers).indexOf(documentId) !== -1;

    const getStartTime = (newDocument: PodcastDocument) => {
        const { id, audio: newAudio = null, metadata = null } = newDocument;
        const progress = queryClient.getQueryData<number | null>(['document_progress', id]);
        const newDuration = newAudio?.duration || metadata?.duration || 0;
        return progress && progress < 0.98 ? progress * newDuration : null;
    };

    const load = (
        newDocument: PodcastDocument,
        {
            queue: newQueue = null,
            startTime = null,
            autoPlay = true,
            withoutTracking = false,
        }: LoadOptions = {},
    ) => {
        const element = audioRef.current;
        const url = getAudioUrl(newDocument?.audio);
        if (element === null || url === null) {
            return;
        }
        if (newQueue !== null) {
            setQueue(newQueue);
        }
        if (newDocument.id !== documentId) {
            const finalStartTime = startTime !== null ? startTime : getStartTime(newDocument);
            setDocument(newDocument);
            setEnded(false);
            setCurrentTime(finalStartTime || 0);
            setDuration(newDocument.audio?.duration || newDocument.metadata?.duration || 0);
            setTrackingDisabled(withoutTracking);
            pendingSeekRef.current =
                finalStartTime !== null && finalStartTime > 0 ? finalStartTime : null;
            element.src = url;
            element.defaultPlaybackRate = rate;
            element.playbackRate = rate;
        } else if (startTime !== null) {
            element.currentTime = startTime;
        }
        if (autoPlay) {
            element.play().catch(() => setPlaying(false));
        }
    };

    const play = () => {
        audioRef.current?.play().catch(() => setPlaying(false));
    };

    const pause = () => {
        audioRef.current?.pause();
    };

    const toggle = () => {
        if (playing) {
            pause();
        } else {
            play();
        }
    };

    const seek = (time: number) => {
        const element = audioRef.current;
        if (element === null) {
            return;
        }
        const newTime = Math.max(0, duration > 0 ? Math.min(duration, time) : time);
        if (pendingSeekRef.current !== null) {
            pendingSeekRef.current = newTime;
        } else {
            element.currentTime = newTime;
        }
        setCurrentTime(newTime);
    };

    const skip = (delta: number) => seek(currentTime + delta);

    const setRate = (newRate: number) => {
        const element = audioRef.current;
        if (element !== null) {
            element.defaultPlaybackRate = newRate;
            element.playbackRate = newRate;
        }
        setRateState(newRate);
    };

    const setMuted = (newMuted: boolean) => {
        if (audioRef.current !== null) {
            audioRef.current.muted = newMuted;
        }
    };

    // Plays an episode of the queue: its snippet has no audio, so the document is fetched first.
    // An episode without audio pauses the audio, so audio and video never play at once; with
    // `keepVideo`, an episode with a video is left to its video too.
    const playItem = (
        item: DocumentSnippet,
        { keepVideo = false }: { keepVideo?: boolean } = {},
    ) => {
        const { id = null, slug = null } = item || {};
        if (id !== null && id === documentId) {
            play();
            return;
        }
        if (slug === null) {
            return;
        }
        fetchDocument(queryClient, api, slug).then((newDocument) => {
            const { video = null } = (newDocument || {}) as PodcastDocument;
            if (hasAudio(newDocument as PodcastDocument) && !(keepVideo && video !== null)) {
                load(newDocument as PodcastDocument, { autoPlay: true });
            } else {
                pause();
            }
        });
    };

    const next = () => {
        if (nextItem !== null) {
            playItem(nextItem);
        }
    };

    const close = () => {
        const element = audioRef.current;
        if (element !== null) {
            element.pause();
            element.removeAttribute('src');
            element.load();
        }
        pendingSeekRef.current = null;
        setDocument(null);
        setQueue([]);
        setPlaying(false);
        setWaiting(false);
        setCurrentTime(0);
        setDuration(0);
    };

    const setPagePlayerVisible = (key: string, visibleDocumentId: string | null) => {
        setPagePlayers((players) => {
            if ((players[key] || null) === visibleDocumentId) {
                return players;
            }
            const otherPlayers = Object.keys(players)
                .filter((playerKey) => playerKey !== key)
                .reduce((map, playerKey) => ({ ...map, [playerKey]: players[playerKey] }), {});
            return visibleDocumentId !== null
                ? { ...otherPlayers, [key]: visibleDocumentId }
                : otherPlayers;
        });
    };

    // Fetches the next episode ahead so it starts right away
    const nextSlug = nextItem?.slug || null;
    useEffect(() => {
        if (nextSlug !== null) {
            fetchDocument(queryClient, api, nextSlug).catch(() => null);
        }
    }, [nextSlug]);

    // Document progress, the same events as the videos
    const onTrackingProgress = (progressStep: number) => {
        if (document === null) {
            return;
        }
        queryClient.invalidateQueries({ queryKey: ['document_progress', documentId] });
        if (tracking === null) {
            return;
        }
        tracking.trackEvent('Document', 'progress', title, null, {
            documentProgress: progressStep * 100,
            document,
        });
        if (!startedRef.current[documentId]) {
            startedRef.current[documentId] = true;
            tracking.trackEvent('Document', 'start', title, null, {
                document,
            });
        }
    };

    useVideoTracking(
        document !== null
            ? {
                  playing,
                  paused: !playing,
                  ended,
                  currentTime,
                  duration,
              }
            : null,
        {
            provider: 'audio',
            id: audio?.id || null,
            url: getAudioUrl(audio),
            title,
            disabled: trackingDisabled || document === null,
            progressSteps,
            onProgress: onTrackingProgress,
        },
    );

    const onEnded = () => {
        setPlaying(false);
        setEnded(true);
        if (!trackingDisabled && tracking !== null && document !== null) {
            tracking.trackEvent('Document', 'complete', title, null, {
                documentProgress: 100,
                document,
            });
        }
        if (autoplayNext) {
            next();
        }
    };

    const onLoadedMetadata = () => {
        const element = audioRef.current;
        if (element === null) {
            return;
        }
        if (Number.isFinite(element.duration) && element.duration > 0) {
            setDuration(element.duration);
        }
        if (pendingSeekRef.current !== null) {
            element.currentTime = pendingSeekRef.current;
            pendingSeekRef.current = null;
        }
    };

    // Lock screen and headphones controls
    const actionsRef = useRef({ play, pause, skip, seek, next });
    actionsRef.current = { play, pause, skip, seek, next };
    const hasNext = nextItem !== null;
    useEffect(() => {
        if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) {
            return () => {};
        }
        const { mediaSession } = navigator;
        if (document === null) {
            mediaSession.metadata = null;
            return () => {};
        }
        const { image = null, metadata = null } = document;
        const { collections = [] } = metadata || {};
        const [collection = null] = collections;
        const serie = collection?.serie || collection;
        mediaSession.metadata = new MediaMetadata({
            title: document.title,
            artist: serie?.title || '',
            album: serie?.title || '',
            artwork: image !== null ? [{ src: image.url }] : [],
        });
        const handlers: [MediaSessionAction, MediaSessionActionHandler][] = [
            ['play', () => actionsRef.current.play()],
            ['pause', () => actionsRef.current.pause()],
            ['seekbackward', ({ seekOffset = 15 }) => actionsRef.current.skip(-seekOffset)],
            ['seekforward', ({ seekOffset = 30 }) => actionsRef.current.skip(seekOffset)],
            ['seekto', ({ seekTime = 0 }) => actionsRef.current.seek(seekTime)],
            ['nexttrack', hasNext ? () => actionsRef.current.next() : null],
        ];
        handlers.forEach(([action, handler]) => {
            try {
                mediaSession.setActionHandler(action, handler);
            } catch (e) {
                // The browser does not support this action
            }
        });
        return () => {
            handlers.forEach(([action]) => {
                try {
                    mediaSession.setActionHandler(action, null);
                } catch (e) {
                    // The browser does not support this action
                }
            });
        };
    }, [document, hasNext]);

    const value: AudioPlayerContextValue = {
        document,
        queue,
        nextItem,
        playing,
        loading: waiting && playing,
        currentTime,
        duration,
        rate,
        muted,
        autoplayNext,
        pagePlayerVisible,
        load,
        play,
        pause,
        toggle,
        seek,
        skip,
        setRate,
        setMuted,
        setQueue,
        setAutoplayNext,
        playItem,
        next,
        close,
        setPagePlayerVisible,
    };

    return (
        <AudioPlayerContext value={value}>
            {children}
            <audio
                ref={audioRef}
                preload="metadata"
                onLoadedMetadata={onLoadedMetadata}
                onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
                onPlay={() => {
                    setPlaying(true);
                    setEnded(false);
                }}
                onPause={() => setPlaying(false)}
                onWaiting={() => setWaiting(true)}
                onPlaying={() => setWaiting(false)}
                onCanPlay={() => setWaiting(false)}
                onEnded={onEnded}
                onRateChange={() => setRateState(audioRef.current?.playbackRate || 1)}
                onVolumeChange={() => setMutedState(audioRef.current?.muted || false)}
            />
        </AudioPlayerContext>
    );
}
